import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import '../styles/AnalyticsDashboard.css';

// Register ChartJS elements
ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement,
  Title, Tooltip, Legend, Filler
);

const AnalyticsDashboard = () => {
  // Line Chart: Sales Overview
  const salesLineData = {
    labels: ['01 May', '04 May', '07 May', '10 May', '13 May', '16 May', '20 May'],
    datasets: [{
      fill: true,
      data: [15000, 35000, 25000, 45000, 30000, 55000, 85000],
      borderColor: '#3b82f6',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      borderWidth: 2,
      pointBackgroundColor: '#3b82f6',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 4,
      tension: 0.4,
    }],
  };

  // Line Chart: Profit Trend
  const profitLineData = {
    labels: ['01 May', '04 May', '07 May', '10 May', '13 May', '16 May', '20 May'],
    datasets: [{
      fill: true,
      data: [10000, 18000, 12000, 22000, 15000, 32000, 48000],
      borderColor: '#22c55e',
      backgroundColor: 'rgba(34, 197, 94, 0.1)',
      borderWidth: 2,
      pointBackgroundColor: '#22c55e',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 4,
      tension: 0.4,
    }],
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#848a95', font: { size: 10 } } },
      y: { 
        grid: { color: '#f4f5f8' }, 
        ticks: { color: '#848a95', font: { size: 10 }, callback: (value) => value === 0 ? '₹0' : `₹${value / 1000}K` },
      },
    },
  };

  // Bar Chart: Sales vs Purchases
  const barData = {
    labels: ['01 May', '04 May', '07 May', '10 May', '13 May', '16 May', '20 May'],
    datasets: [
      {
        label: 'Sales',
        data: [50000, 65000, 55000, 75000, 60000, 85000, 95000],
        backgroundColor: '#3b82f6',
        borderRadius: 4,
        barPercentage: 0.6,
        categoryPercentage: 0.7,
      },
      {
        label: 'Purchases',
        data: [30000, 45000, 35000, 50000, 40000, 55000, 65000],
        backgroundColor: '#22c55e',
        borderRadius: 4,
        barPercentage: 0.6,
        categoryPercentage: 0.7,
      }
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#848a95', font: { size: 10 } } },
      y: { grid: { color: '#f4f5f8', borderDash: [4, 4] }, ticks: { color: '#848a95', font: { size: 10 }, callback: (v) => v === 0 ? '0' : `₹${v/1000}K` } },
    },
  };

  // Doughnut: Sales by Category
  const salesDonutData = {
    labels: ['Electronics', 'Home Appliances', 'Mobile & Accessories', 'Fashion', 'Others', 'Stationery', 'Sports'],
    datasets: [{
      data: [245300, 185450, 145600, 120600, 77480, 60000, 40000],
      backgroundColor: ['#3b82f6', '#22c55e', '#a855f7', '#f59e0b', '#848a95', '#14b8a6', '#64748b'],
      borderWidth: 0,
      cutout: '72%',
    }],
  };

  // Doughnut: Expenses by Category
  const expensesDonutData = {
    labels: ['Rent', 'Salaries', 'Utilities', 'Transport', 'Marketing', 'Others'],
    datasets: [{
      data: [75000, 120000, 24970, 5800, 5250, 68100],
      backgroundColor: ['#3b82f6', '#22c55e', '#f59e0b', '#0ea5e9', '#ef4444', '#848a95'],
      borderWidth: 0,
      cutout: '72%',
    }],
  };

  const donutOptions = { plugins: { legend: { display: false } }, maintainAspectRatio: false };

  // Table Data
  const topProducts = [
    { id: 1, name: "iPhone 14 (128GB)", qty: 45, sales: "₹67,500", img: "fa-solid fa-mobile-screen" },
    { id: 2, name: "Samsung Galaxy S23", qty: 38, sales: "₹56,050", img: "fa-solid fa-mobile" },
    { id: 3, name: "HP Laptop 15s", qty: 32, sales: "₹44,800", img: "fa-solid fa-laptop" },
    { id: 4, name: "Boat Airdopes 141", qty: 85, sales: "₹25,500", img: "fa-solid fa-headphones-simple" },
    { id: 5, name: "Sony WH-CH520", qty: 40, sales: "₹23,600", img: "fa-solid fa-headset" }
  ];

  return (
    <div className="aly-wrapper">
      
      {/* 1. Top KPI Grid */}
      <div className="aly-kpi-grid">
        <div className="aly-card aly-kpi-card">
          <div className="aly-icon-box aly-bg-blue-light"><i className="fa-solid fa-chart-column aly-text-blue"></i></div>
          <div className="aly-kpi-info">
            <span className="aly-kpi-title">Total Sales</span>
            <h2 className="aly-kpi-value">₹8,75,430</h2>
            <div className="aly-kpi-meta"><span className="aly-trend-green"><i className="fa-solid fa-arrow-up"></i> 18.6%</span> vs 01 Apr - 30 Apr 2025</div>
          </div>
        </div>
        <div className="aly-card aly-kpi-card">
          <div className="aly-icon-box aly-bg-green-light"><i className="fa-solid fa-cart-shopping aly-text-green"></i></div>
          <div className="aly-kpi-info">
            <span className="aly-kpi-title">Total Purchases</span>
            <h2 className="aly-kpi-value">₹5,45,230</h2>
            <div className="aly-kpi-meta"><span className="aly-trend-green"><i className="fa-solid fa-arrow-up"></i> 12.4%</span> vs 01 Apr - 30 Apr 2025</div>
          </div>
        </div>
        <div className="aly-card aly-kpi-card">
          <div className="aly-icon-box aly-bg-orange-light"><i className="fa-solid fa-indian-rupee-sign aly-text-orange"></i></div>
          <div className="aly-kpi-info">
            <span className="aly-kpi-title">Gross Profit</span>
            <h2 className="aly-kpi-value">₹3,30,200</h2>
            <div className="aly-kpi-meta"><span className="aly-trend-green"><i className="fa-solid fa-arrow-up"></i> 22.7%</span> vs 01 Apr - 30 Apr 2025</div>
          </div>
        </div>
        <div className="aly-card aly-kpi-card">
          <div className="aly-icon-box aly-bg-purple-light"><i className="fa-solid fa-wallet aly-text-purple"></i></div>
          <div className="aly-kpi-info">
            <span className="aly-kpi-title">Total Expenses</span>
            <h2 className="aly-kpi-value">₹3,25,120</h2>
            <div className="aly-kpi-meta"><span className="aly-trend-red"><i className="fa-solid fa-arrow-down"></i> 5.3%</span> vs 01 Apr - 30 Apr 2025</div>
          </div>
        </div>
        <div className="aly-card aly-kpi-card">
          <div className="aly-icon-box aly-bg-teal-light"><i className="fa-solid fa-chart-line aly-text-teal"></i></div>
          <div className="aly-kpi-info">
            <span className="aly-kpi-title">Net Profit</span>
            <h2 className="aly-kpi-value">₹1,05,080</h2>
            <div className="aly-kpi-meta"><span className="aly-trend-green"><i className="fa-solid fa-arrow-up"></i> 35.8%</span> vs 01 Apr - 30 Apr 2025</div>
          </div>
        </div>
      </div>

      {/* 2. Middle Grid 1: Sales Line, Sales Donut, Sales vs Purchases */}
      <div className="aly-grid-3">
        
        {/* Sales Overview Line */}
        <div className="aly-card aly-p-24 aly-flex-col">
          <div className="aly-card-header">
            <h3 className="aly-card-title">Sales Overview</h3>
            <div className="aly-dropdown">This Month <i className="fa-solid fa-chevron-down"></i></div>
          </div>
          <div className="aly-chart-head-metrics">
            <div className="aly-cm-icon"><i className="fa-solid fa-arrow-up-right-dots"></i></div>
            <div className="aly-cm-text">
              <span>Total Sales <span className="aly-badge-green"><i className="fa-solid fa-arrow-up"></i> 18.6%</span></span>
              <h2>₹8,75,430 <small>vs 01 Apr - 30 Apr 2025</small></h2>
            </div>
          </div>
          <div className="aly-chart-container"><Line data={salesLineData} options={lineOptions} /></div>
        </div>

        {/* Sales by Category Donut */}
        <div className="aly-card aly-p-24 aly-flex-col">
          <div className="aly-card-header">
            <h3 className="aly-card-title">Sales by Category</h3>
            <div className="aly-dropdown">This Month <i className="fa-solid fa-chevron-down"></i></div>
          </div>
          <div className="aly-donut-centered">
            <div className="aly-donut-wrapper">
              <Doughnut data={salesDonutData} options={donutOptions} />
              <div className="aly-donut-center">
                <span className="aly-dc-label">Total</span>
                <strong className="aly-dc-value">₹8,75,430</strong>
              </div>
            </div>
            <div className="aly-donut-legend aly-grid-2-col">
              <div className="aly-leg-item"><span className="aly-dot aly-bg-blue"></span><div><strong>Electronics</strong><span>₹2,45,300 (28.0%)</span></div></div>
              <div className="aly-leg-item"><span className="aly-dot aly-bg-green"></span><div><strong>Home Appliances</strong><span>₹1,85,450 (21.2%)</span></div></div>
              <div className="aly-leg-item"><span className="aly-dot aly-bg-purple"></span><div><strong>Mobile & Accessories</strong><span>₹1,45,600 (16.6%)</span></div></div>
              <div className="aly-leg-item"><span className="aly-dot aly-bg-orange"></span><div><strong>Fashion</strong><span>₹1,20,600 (13.8%)</span></div></div>
              <div className="aly-leg-item"><span className="aly-dot aly-bg-gray"></span><div><strong>Others</strong><span>₹77,480 (8.8%)</span></div></div>
              <div className="aly-leg-item"><span className="aly-dot aly-bg-teal"></span><div><strong>Stationery</strong><span>₹60,000 (6.8%)</span></div></div>
              <div className="aly-leg-item"><span className="aly-dot" style={{backgroundColor:'#64748b'}}></span><div><strong>Sports</strong><span>₹40,000 (4.6%)</span></div></div>
            </div>
          </div>
        </div>

        {/* Sales vs Purchases Bar */}
        <div className="aly-card aly-p-24 aly-flex-col">
          <div className="aly-card-header">
            <h3 className="aly-card-title">Sales vs Purchases</h3>
            <div className="aly-dropdown">This Month <i className="fa-solid fa-chevron-down"></i></div>
          </div>
          <div className="aly-custom-bar-legend">
            <div className="aly-leg-h"><span className="aly-dot aly-bg-blue"></span> Sales</div>
            <div className="aly-leg-h"><span className="aly-dot aly-bg-green"></span> Purchases</div>
          </div>
          <div className="aly-chart-container" style={{marginTop: '10px'}}><Bar data={barData} options={barOptions} /></div>
        </div>

      </div>

      {/* 3. Middle Grid 2: Table, Profit Line, Expenses Donut */}
      <div className="aly-grid-3">
        
        {/* Top Selling Products Table */}
        <div className="aly-card aly-p-24 aly-flex-col">
          <div className="aly-card-header">
            <h3 className="aly-card-title">Top Selling Products</h3>
            <div className="aly-dropdown">This Month <i className="fa-solid fa-chevron-down"></i></div>
          </div>
          <div className="aly-table-wrapper" style={{flex: 1}}>
            <table className="aly-table">
              <thead><tr><th>#</th><th>Product</th><th className="aly-align-right">Qty Sold</th><th className="aly-align-right">Sales</th></tr></thead>
              <tbody>
                {topProducts.map(p => (
                  <tr key={p.id}>
                    <td className="aly-text-muted">{p.id}</td>
                    <td className="aly-fw-600 aly-flex-center"><i className={`${p.img} aly-text-muted aly-mr-8`}></i> {p.name}</td>
                    <td className="aly-align-right">{p.qty}</td>
                    <td className="aly-align-right aly-fw-600">{p.sales}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="aly-mt-16"><a href="#" className="aly-link">View all products report <i className="fa-solid fa-arrow-right"></i></a></div>
        </div>

        {/* Profit Trend Line */}
        <div className="aly-card aly-p-24 aly-flex-col">
          <div className="aly-card-header">
            <h3 className="aly-card-title">Profit Trend</h3>
            <div className="aly-dropdown">This Month <i className="fa-solid fa-chevron-down"></i></div>
          </div>
          <div className="aly-chart-head-metrics aly-mb-16">
            <div className="aly-cm-text">
              <span style={{fontSize: '12px', color: '#848a95'}}>Net Profit</span>
              <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                <h2 style={{margin:0}}>₹1,05,080</h2>
                <span className="aly-trend-green" style={{fontSize:'12px'}}><i className="fa-solid fa-arrow-up"></i> 35.8% <span style={{color:'#848a95', fontWeight:400, marginLeft:'4px'}}>vs 01 Apr - 30 Apr 2025</span></span>
              </div>
            </div>
          </div>
          <div className="aly-chart-container"><Line data={profitLineData} options={lineOptions} /></div>
        </div>

        {/* Expenses Donut */}
        <div className="aly-card aly-p-24 aly-flex-col">
          <div className="aly-card-header">
            <h3 className="aly-card-title">Expenses by Category</h3>
            <div className="aly-dropdown">This Month <i className="fa-solid fa-chevron-down"></i></div>
          </div>
          <div className="aly-donut-horizontal">
            <div className="aly-donut-wrapper">
              <Doughnut data={expensesDonutData} options={donutOptions} />
              <div className="aly-donut-center">
                <span className="aly-dc-label">Total</span>
                <strong className="aly-dc-value">₹3,25,120</strong>
              </div>
            </div>
            <div className="aly-donut-legend">
              <div className="aly-leg-item"><span className="aly-dot aly-bg-blue"></span><div><strong>Rent</strong><span>₹75,000 (23.1%)</span></div></div>
              <div className="aly-leg-item"><span className="aly-dot aly-bg-green"></span><div><strong>Salaries</strong><span>₹1,20,000 (36.9%)</span></div></div>
              <div className="aly-leg-item"><span className="aly-dot aly-bg-orange"></span><div><strong>Utilities</strong><span>₹24,970 (7.7%)</span></div></div>
              <div className="aly-leg-item"><span className="aly-dot aly-bg-teal"></span><div><strong>Transport</strong><span>₹5,800 (1.8%)</span></div></div>
              <div className="aly-leg-item"><span className="aly-dot aly-bg-red"></span><div><strong>Marketing</strong><span>₹5,250 (1.6%)</span></div></div>
              <div className="aly-leg-item"><span className="aly-dot aly-bg-gray"></span><div><strong>Others</strong><span>₹68,100 (20.9%)</span></div></div>
            </div>
          </div>
        </div>

      </div>

      {/* 4. Bottom Grid: Summary & Insights */}
      <div className="aly-bottom-grid">
        
        {/* Business Summary */}
        <div className="aly-card aly-p-24">
          <h3 className="aly-card-title aly-mb-20">Business Summary</h3>
          <div className="aly-bs-grid">
            <div className="aly-bs-item">
              <div className="aly-icon-sm aly-bg-blue-light aly-text-blue"><i className="fa-solid fa-file-invoice"></i></div>
              <div className="aly-bs-text">
                <span>Total Invoices</span>
                <strong>158</strong>
                <span className="aly-trend-green aly-trend-sm"><i className="fa-solid fa-arrow-up"></i> 8.2%</span>
              </div>
            </div>
            <div className="aly-bs-item">
              <div className="aly-icon-sm aly-bg-green-light aly-text-green"><i className="fa-solid fa-receipt"></i></div>
              <div className="aly-bs-text">
                <span>Avg. Invoice Value</span>
                <strong>₹5,542</strong>
                <span className="aly-trend-green aly-trend-sm"><i className="fa-solid fa-arrow-up"></i> 9.4%</span>
              </div>
            </div>
            <div className="aly-bs-item">
              <div className="aly-icon-sm aly-bg-purple-light aly-text-purple"><i className="fa-solid fa-users"></i></div>
              <div className="aly-bs-text">
                <span>New Customers</span>
                <strong>24</strong>
                <span className="aly-trend-green aly-trend-sm"><i className="fa-solid fa-arrow-up"></i> 20.0%</span>
              </div>
            </div>
            <div className="aly-bs-item">
              <div className="aly-icon-sm aly-bg-orange-light aly-text-orange"><i className="fa-solid fa-user-check"></i></div>
              <div className="aly-bs-text">
                <span>Repeat Customers</span>
                <strong>68</strong>
                <span className="aly-trend-green aly-trend-sm"><i className="fa-solid fa-arrow-up"></i> 12.7%</span>
              </div>
            </div>
            <div className="aly-bs-item">
              <div className="aly-icon-sm aly-bg-teal-light aly-text-teal"><i className="fa-solid fa-box"></i></div>
              <div className="aly-bs-text">
                <span>Inventory Value</span>
                <strong>₹12,45,300</strong>
                <span className="aly-trend-green aly-trend-sm"><i className="fa-solid fa-arrow-up"></i> 6.1%</span>
              </div>
            </div>
            <div className="aly-bs-item">
              <div className="aly-icon-sm aly-bg-red-light aly-text-red"><i className="fa-solid fa-triangle-exclamation"></i></div>
              <div className="aly-bs-text">
                <span>Low Stock Items</span>
                <strong>18</strong>
                <a href="#" className="aly-link" style={{fontSize:'11px', marginTop:'4px'}}>View details</a>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Insights */}
        <div className="aly-card aly-p-24 aly-flex-col">
          <h3 className="aly-card-title aly-mb-20">Recent Insights</h3>
          <ul className="aly-insights-list">
            <li><i className="fa-solid fa-chart-line aly-text-blue"></i> Sales are up by 18.6% compared to last month.</li>
            <li><i className="fa-solid fa-crown aly-text-purple"></i> Mobile & Accessories is your top performing category.</li>
            <li><i className="fa-solid fa-percent aly-text-green"></i> Gross profit margin is 37.7%.</li>
            <li><i className="fa-solid fa-triangle-exclamation aly-text-orange"></i> 4 products are running low on stock.</li>
          </ul>
          <div className="aly-mt-auto"><a href="#" className="aly-link">View detailed analytics report <i className="fa-solid fa-arrow-right"></i></a></div>
        </div>

      </div>

    </div>
  );
};

export default AnalyticsDashboard;